/**
 * Shared constants for NFC reader communication URLs and memory bank IDs
 * used by both read-device and read-device-multi-driver components.
 */

export const READER_TYPE_SIMPLESET = "SimpleSet";
export const READER_TYPE_DALI = "DALI";

export interface ReaderCapabilities {
  interfaces: Record<string, { readWrite: string[]; readOnly: string[] }>;
  featureMemoryBanks: Record<string, string[]>;
}

export function readerFeatureFamily(feature: string): string {
  const family = feature.replace(/^led-features:/, "").split("-")[0].toLowerCase();
  if (["lumin", "luminfo", "luminaire info"].includes(family)) {
    return "luminfoformat";
  }
  return family.replace(/^luminfoformat\d*$/, "luminfoformat").replace(/^srpsu$/, "dalipsu");
}

export function decodeReaderCapabilities(schema: unknown): ReaderCapabilities | undefined {
  const properties = (schema as { metadata?: { properties?: { key: string; value: string }[] } })?.metadata?.properties;
  if (!Array.isArray(properties) || !properties.some(property => property.key === "interface")) {
    return undefined;
  }
  const capabilities: ReaderCapabilities = { interfaces: {}, featureMemoryBanks: {} };
  const strings = (value: unknown): string[] => {
    if (!Array.isArray(value) || !value.every(item => typeof item === "string")) {
      throw new Error("Expected a string array in reader capabilities");
    }
    return value;
  };
  const decode = (key: string): Record<string, unknown>[] => {
    const property = properties.find(item => item.key === key);
    if (!property) { throw new Error(`Missing schema property: ${key}`); }
    const decoded: unknown = JSON.parse(atob(property.value));
    if (!Array.isArray(decoded) || !decoded.every(item => item && typeof item === "object" && !Array.isArray(item))) {
      throw new Error(`Invalid schema property: ${key}`);
    }
    return decoded;
  };
  try {
    for (const entry of decode("interface")) {
      for (const [name, value] of Object.entries(entry)) {
        const access = value as Record<string, unknown>;
        capabilities.interfaces[name.toLowerCase()] = {
          readWrite: strings(access["rw-features"]).map(readerFeatureFamily),
          readOnly: strings(access["ro-features"]).map(readerFeatureFamily),
        };
      }
    }
    for (const entry of decode("feature2MbRelation")) {
      for (const [feature, banks] of Object.entries(entry)) {
        capabilities.featureMemoryBanks[readerFeatureFamily(feature)] = strings(banks);
      }
    }
    for (const access of Object.values(capabilities.interfaces)) {
      for (const feature of [...access.readWrite, ...access.readOnly]) {
        if (!capabilities.featureMemoryBanks[feature]) {
          throw new Error(`Missing memory-bank mapping for ${feature}`);
        }
      }
    }
    return capabilities;
  } catch (error) {
    console.warn("Invalid reader capabilities; interface access disabled", error);
    return { interfaces: {}, featureMemoryBanks: {} };
  }
}

/** DCS reports the bus of every peripheral in `peripheralInterface` ("NFC" | "DALI"). */
export function resolveReaderType(peripheral: { peripheralInterface?: string }): string {
  return String(peripheral?.peripheralInterface ?? "").toUpperCase() === "DALI"
    ? READER_TYPE_DALI
    : READER_TYPE_SIMPLESET;
}

/** Keeps the reader type the user picked; SimpleSet is the default when nothing was chosen yet. */
export function preserveReaderType(previous: { readerType?: string } | null | undefined): string {
  return previous?.readerType === READER_TYPE_DALI ? READER_TYPE_DALI : READER_TYPE_SIMPLESET;
}

/** DCS addresses each bus by a path segment: `nfc` for SimpleSet, `dali` for Lunatone. */
export function readerInterfaceSegment(readerType?: string): string {
  return readerType === READER_TYPE_DALI ? "dali" : "nfc";
}

/** `/peripheral/v1/{nfc|dali}/{peripheralId}/devices` for the active reader. */
export function buildDeviceInfoUrl(readerType?: string, refresh = true): string {
  const suffix = refresh ? "?refresh=true" : "";
  return `/peripheral/v1/${readerInterfaceSegment(readerType)}/{peripheralId}/devices${suffix}`;
}

/** DCS returns the device identifier in a bus-specific field. */
export function deviceIdField(readerType?: string): "deviceDaliId" | "deviceNfcId" {
  return readerType === READER_TYPE_DALI ? "deviceDaliId" : "deviceNfcId";
}

/** Drops banks the active bus cannot reach from a read request. */
export function filterMemoryBanksForReader(
  readerType?: string,
  capabilities?: ReaderCapabilities
): { memoryBankId: string }[] {
  if (capabilities) {
    const access = capabilities.interfaces[readerType === READER_TYPE_DALI ? "dali" : "simpleset"];
    const features = access ? [...access.readWrite, ...access.readOnly] : [];
    return [...new Set(features.flatMap(feature => capabilities.featureMemoryBanks[feature] ?? []))]
      .map(memoryBankId => ({ memoryBankId }));
  }
  return [];
}

/** Drops features the active bus cannot configure from the feature landing page. */
export function filterFeaturesForReader(features: string[], readerType?: string, capabilities?: ReaderCapabilities): string[] {
  if (capabilities && Array.isArray(features)) {
    const access = capabilities.interfaces[readerType === READER_TYPE_DALI ? "dali" : "simpleset"];
    const writable = new Set(access?.readWrite ?? []);
    return features.filter(feature => writable.has(readerFeatureFamily(feature)));
  }
  return [];
}

/**
 * `/device/v1/{nfc|dali}/{peripheralId}/{deviceNfcId}<suffix>` for the active reader.
 * The `{deviceNfcId}` token is kept for both buses so existing call sites keep working.
 */
export function buildDeviceUrl(readerType: string | undefined, suffix: string): string {
  return `/device/v1/${readerInterfaceSegment(readerType)}/{peripheralId}/{deviceNfcId}${suffix}`;
}

export const READER_BASE_URL = "http://localhost:2020";
export const READER_START_URL = "/server/v1/status";
export const READER_LIST_URL = "/peripheral/v1/types";
export const READER_DATA_URL = "/device/v1/NFC/LCN9600/dummy/memorybank/114";
export const DEVICE_INFO_URL = "/peripheral/v1/nfc/{peripheralId}/devices";
export const DEVICE_INFO_REFRESH_URL = "/peripheral/v1/nfc/{peripheralId}/devices?refresh=true";
export const MEMORY_BANK_URL = "/device/v1/nfc/{peripheralId}/{deviceNfcId}/memorybanks/metadata";
export const CORRUPT_BIT_URL = "/device/v1/nfc/{peripheralId}/{deviceNfcId}/memorybanks/corrupt";
export const WRITING_IN_DEVICE_URL = "/device/v1/nfc/{peripheralId}/{deviceNfcId}/memorybanks";
export const FORMATTED_MEMORY_BANK_URL = "/device/v1/nfc/{peripheralId}/{deviceNfcId}/memorybanks/formatted";
export const PAYLOAD_URL = "/device/v1/nfc/{peripheralId}/{deviceNfcId}/memorybanks";
export const IMPORT_PAYLOAD_URL = "feature/api/v1/configuration/{configurationId}/feature-payload";
export const IMPORT_PAYLOAD_URL_NE = "feature/api/v1/configuration/{configurationId}/feature-payload/not-encrypted";

/** Common memory bank IDs shared by all reader components */
export const COMMON_MEMORY_BANKS = [
  { memoryBankId: "114-0" },  // AOC
  { memoryBankId: "73-0" },   // ALO
  { memoryBankId: "37-0" },   // ALO MIN
  { memoryBankId: "14-0" },   // CLO/CLOLITE
  { memoryBankId: "149-0" },  // AOC PWM
  { memoryBankId: "29-0" },   // AST
  { memoryBankId: "1-0" },    // LUMIN
  { memoryBankId: "35-0" },   // SRPSU
  { memoryBankId: "19-0" },   // ZTV
  { memoryBankId: "52-0" },
  { memoryBankId: "13-0" },   // LUMIN
  { memoryBankId: "15-0" },   // LINESWITCH
  { memoryBankId: "18-0" },   // AMPDIM
  { memoryBankId: "75-0" },   // DC EMERGENCY
  { memoryBankId: "74-0" },   // DTL
  { memoryBankId: "113-0" },  // DTL
  { memoryBankId: "36-0" },   // Coded Mains
];

/** Memory bank for Dali 253 Maintenance */
export const DALI253_MEMORY_BANK = { memoryBankId: "207-0" };
